"use client";
import React, { useEffect, useState } from "react";
import { Pagination, Spin, Empty } from "antd";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, ArrowRight } from "lucide-react";
import CommonService from "@/services/api/CommonService";
import {
  FaArrowRight,
  FaCalendarDays,
  FaRegCalendarDays,
  FaTag,
} from "react-icons/fa6";
import { motion } from "framer-motion";

export default function NoticesPage() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchNotices = async (page = 1) => {
    setLoading(true);
    try {
      const res = await CommonService.getNotices({ page, per_page: pageSize });
      if (res?.data) {
        setNotices(res.data);
        setTotalItems(res.total || 0);
        setCurrentPage(res.current_page || page);
        setPageSize(res.per_page || pageSize);
      }
    } catch (err) {
      console.error("Error fetching notices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices(currentPage);
  }, [currentPage]);

  return (
    <div className="notices-page section_padding-48">
      <div className="container">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="leaderboard-wrap flex-[4] p-4 bg-white rounded-lg shadow-md">
            <div className="grid grid-cols-12 md:gap-6 gap-4">
              <div className="noticeboard-left-part lg:col-span-12 col-span-12">
                <div className="title-search-area">
                  <h2 className="card-title !mb-0 flex gap-2 items-center [.dark_&]:text-d-white">
                    <Image
                      src="/images/home-page/notice-icon.avif"
                      alt="Notice Icon"
                      width={31}
                      height={31}
                    />
                    নোটিশ বোর্ড
                  </h2>
                </div>

                <div className="fa-card-sm">
                  <div className="overflow-x-auto">
                    {loading ? (
                      <div className="text-center py-8">
                        <Spin size="large" />
                      </div>
                    ) : notices.length === 0 ? (
                      <Empty
                        description="কোনো নোটিশ পাওয়া যায়নি।"
                        className="py-10"
                      />
                    ) : (
                      <table className="fa-table text-center">
                        <thead>
                          <tr>
                            <th className="px-4 py-2 w-[40%]">নোটিশের বিষয়</th>
                            <th className="px-4 py-2 w-[20%]">তারিখ</th>
                            <th className="px-4 py-2 w-[20%]">অ্যাকশন</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notices.map((notice) => (
                            <tr key={notice.id} className="result-tr">
                              <td className="px-4 py-2 !text-start">
                                {notice.type === "link" ? (
                                  <a
                                    href={notice.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {notice.name}
                                  </a>
                                ) : notice.type === "doc" ? (
                                  <a
                                    href={notice.docs_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {notice.name}
                                  </a>
                                ) : (
                                  notice.name
                                )}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <div className="flex justify-center items-center gap-1">
                                  <FaCalendarDays className="inline-block text-gray-500" />
                                  {notice.date}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <Link
                                  href={`/notice-board/${
                                    notice.slug || notice.id
                                  }`}
                                >
                                  <button className="written-details-btn flex items-center gap-1 justify-center !m-auto">
                                    <FaArrowRight className="inline-block" />
                                    বিস্তারিত
                                  </button>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {totalItems > pageSize && (
                    <div className="fa-pagination">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalItems}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                        itemRender={(page, type, originalElement) => {
                          if (type === "prev") {
                            return (
                              <a>
                                <ArrowLeft />
                              </a>
                            );
                          }
                          if (type === "next") {
                            return (
                              <a>
                                <ArrowRight />
                              </a>
                            );
                          }
                          return originalElement;
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="leaderboard-wrap flex-[2] p-4 bg-white rounded-lg shadow-md">
            <div className="notice-board-right-part lg:col-span-4 col-span-12">
              <div className="title-search-area">
                <h2 className="card-title !mb-0 flex gap-2 items-center [.dark_&]:text-d-white">
                  <Image
                    src="/images/home-page/notice-icon.avif"
                    alt="Notice Icon"
                    width={31}
                    height={31}
                  />
                  সর্বশেষ নোটিশ
                </h2>
              </div>

              <div className="fa-card-sm leatest-notice-card">
                <div className="overflow-hidden h-48">
                  <motion.div
                    initial={{ y: "100%" }}
                    animate={{ y: "-100%" }}
                    transition={{
                      repeat: Infinity,
                      repeatType: "loop",
                      ease: "linear",
                      duration: 15,
                    }}
                    className="space-y-4"
                  >
                    {notices.map((notice) => (
                      <Link
                        key={notice.id}
                        href={`/notice-board/${notice.slug || notice.id}`}
                        className="leatest-notice-item"
                      >
                        <span className="text-red-500 font-bold ">NEW</span>
                        <p className="!mb-0">{notice.name}</p>
                      </Link>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
